/**
 * REST client for Copilot conversation persistence (Web version).
 * Uses the standard browser fetch API. Auth is the user's bearer token plus
 * the tenant header; the browser never carries a service key (W0-05).
 */


import { fetchCopilotBff } from "@/lib/copilot-session-fetch";
import { getApiBase } from "@/lib/api-base";

function headers(token: string, tenantId: string): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    "X-Tenant": tenantId,
  };
}


export type ApiConversation = {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  messages_count?: number;
};

export type ApiMessage = {
  id: string;
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  tool_calls: unknown[] | null;
  created_at: string;
};

export type AiContext = {
  llm_provider: string;
  llm_model: string;
  balance: number;
  tenant_id: string;
  user_id: string | null;
  limit_exceeded: boolean;
  usage_today: number;
  cowork_lite_limit: number;
  is_copilot_pro: boolean;
};

export async function getAiContext(
  token: string,
  tenantId: string,
): Promise<AiContext | null> {
  try {
    const res = await fetchCopilotBff(`${getApiBase()}/api/v1/session/ai/context`, {
      headers: headers(token, tenantId),
    });
    if (!res.ok) return null;
    return (await res.json()) as AiContext;
  } catch {
    return null;
  }
}

// ── Conversations CRUD ──

export async function listConversations(
  token: string,
  tenantId: string,
): Promise<ApiConversation[]> {
  const res = await fetchCopilotBff(`${getApiBase()}/api/v1/copilot/conversations`, {
    headers: headers(token, tenantId),
  });
  if (!res.ok) return [];
  const json = (await res.json()) as { data: ApiConversation[] };
  return json.data ?? [];
}

export async function createConversation(
  token: string,
  tenantId: string,
  title?: string,
): Promise<ApiConversation | null> {
  const res = await fetchCopilotBff(`${getApiBase()}/api/v1/copilot/conversations`, {
    method: "POST",
    headers: headers(token, tenantId),
    body: JSON.stringify({ title }),
  });
  if (!res.ok) return null;
  return (await res.json()) as ApiConversation;
}

export async function getConversation(
  token: string,
  tenantId: string,
  id: string,
): Promise<(ApiConversation & { messages: ApiMessage[] }) | null> {
  const res = await fetchCopilotBff(`${getApiBase()}/api/v1/copilot/conversations/${id}`, {
    headers: headers(token, tenantId),
  });
  if (!res.ok) return null;
  return (await res.json()) as ApiConversation & { messages: ApiMessage[] };
}

export async function deleteConversation(
  token: string,
  tenantId: string,
  id: string,
): Promise<boolean> {
  const res = await fetchCopilotBff(`${getApiBase()}/api/v1/copilot/conversations/${id}`, {
    method: "DELETE",
    headers: headers(token, tenantId),
  });
  return res.ok;
}

// ── Messages ──

export async function addMessage(
  token: string,
  tenantId: string,
  conversationId: string,
  role: "user" | "assistant",
  content: string,
  toolCalls?: unknown[],
): Promise<ApiMessage | null> {
  const res = await fetchCopilotBff(
    `${getApiBase()}/api/v1/copilot/conversations/${conversationId}/messages`,
    {
      method: "POST",
      headers: headers(token, tenantId),
      body: JSON.stringify({ role, content, tool_calls: toolCalls }),
    },
  );
  if (!res.ok) return null;
  return (await res.json()) as ApiMessage;
}

export async function addMessageWithRetry(
  token: string,
  tenantId: string,
  conversationId: string,
  role: "user" | "assistant",
  content: string,
  toolCalls?: unknown[],
): Promise<ApiMessage | null> {
  // Keep the existing caller API, but an unacknowledged POST is not safe to replay.
  try {
    return await addMessage(token, tenantId, conversationId, role, content, toolCalls);
  } catch {
    return null;
  }
}

export function addMessageBeacon(
  token: string,
  tenantId: string,
  conversationId: string,
  role: "user" | "assistant",
  content: string,
): void {
  const url = `${getApiBase()}/api/v1/copilot/conversations/${conversationId}/messages`;
  try {
    fetchCopilotBff(url, {
      method: "POST",
      headers: headers(token, tenantId),
      body: JSON.stringify({ role, content }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Best-effort during page unload
  }
}
